import { computed, effect, ref } from 'fine-jsx'

import { ACCEPT_VISUAL_FILE_TYPE } from '#constants'
import type { InputEvent } from 'shared/types'
import { useI18n } from 'shared/utils'

import type { MediaAsset } from '../assets/media-asset.ts'
import styles from '../css/index.module.css'

import { AssetBinVideoPreview } from './asset-bin-video-preview.jsx'
import { useEditor, useImportMediaFiles } from './utils.ts'

export const AssetBinVideo = () => {
  const editor = useEditor()
  const importMediaFiles = useImportMediaFiles()
  const { t } = useI18n()
  const getVideoAssets = (): MediaAsset[] =>
    Array.from(editor.doc.assets.values()).filter(
      (asset): asset is MediaAsset =>
        (asset.type === 'asset:media:av' && (!!asset.video || asset.mimeType.startsWith('video/'))) ||
        asset.type === 'asset:media:image',
    )

  const assets = ref<MediaAsset[]>(getVideoAssets())
  const assetSearchQuery = ref('')
  const activeVideo = ref<MediaAsset | undefined>()
  const fileInput = ref<HTMLInputElement>()

  const buttonLabel = importMediaFiles ? 'asset_bin_media_import' : 'asset_bin_media_upload'

  const assetsFiltered = computed(() => {
    const query = assetSearchQuery.value.trim().toLowerCase()
    if (query === '') return assets.value
    return assets.value.filter((asset) => (asset.name ?? '').toLowerCase().includes(query))
  })

  effect((onCleanup) => {
    const assetsUpdate = () => {
      assets.value = getVideoAssets()
    }
    const assetCreateOff = editor.doc.assets.on('asset:create', assetsUpdate)
    const assetDeleteOff = editor.doc.assets.on('asset:delete', assetsUpdate)

    onCleanup(() => {
      assetCreateOff()
      assetDeleteOff()
    })
  })

  const addFiles = async (files: File[]) => {
    const results = await Promise.allSettled(
      files.map(async (file) => {
        await editor.createMediaAsset(file)
      }),
    )

    if (results.some((result) => result.status === 'rejected'))
      // eslint-disable-next-line no-alert -- TODO
      alert(t('error_cannot_play_type'))
  }

  const onInputVideoFile = async (event: InputEvent) => {
    const files = Array.from(event.target.files ?? [])
    event.target.value = ''
    await addFiles(files)
  }

  const onClickUpload = async () => {
    if (importMediaFiles) {
      const files = await importMediaFiles('video')
      if (files.length === 0) return
      await addFiles(files)
      return
    }
    fileInput.value?.click()
  }

  const onSearchInput = (event: InputEvent) => {
    assetSearchQuery.value = event.target.value
  }

  return (
    <div class={styles.panelBody}>
      <button
        type="button"
        class={[styles.wideButton, styles.textBodyBold]}
        aria-label={t(buttonLabel)}
        onClick={() => onClickUpload()}
      >
        <IconMsUploadRounded />
        <span>{t(buttonLabel)}</span>
      </button>
      <input
        ref={fileInput}
        type="file"
        multiple
        accept={ACCEPT_VISUAL_FILE_TYPE}
        class={styles.srOnly}
        tabIndex={-1}
        aria-hidden="true"
        onInput={(e: InputEvent) => onInputVideoFile(e)}
      />
      <input
        type="search"
        value={assetSearchQuery}
        onInput={onSearchInput}
        class={styles.panelInput}
        placeholder={t('asset_bin_media_search_placeholder')}
        aria-label={t('asset_bin_media_search_placeholder')}
      />

      <div class={styles.assetBinAssetsContainer}>
        {() => {
          if (assets.value.length === 0) {
            return <p class={styles.textBodySmall}>{t('asset_bin_media_empty')}</p>
          } else if (assetsFiltered.value.length === 0) {
            return <p class={styles.textBodySmall}>{t('asset_bin_media_search_empty')}</p>
          }
          return assetsFiltered.value.map((asset) => (
            <button
              type="button"
              class={[styles.assetBinAsset, styles.assetBinSanitize]}
              onClick={() => (activeVideo.value = asset)}
            >
              <img src={asset.thumbnailUri} alt="" class={styles.assetBinThumbnail} />
              <span class={styles.assetBinName}>{asset.name}</span>
            </button>
          ))
        }}
      </div>
      {() =>
        activeVideo.value && (
          <AssetBinVideoPreview asset={activeVideo.value} onClose={() => (activeVideo.value = undefined)} />
        )
      }
    </div>
  )
}
